import t from "@repo/localization/string.macro";
import {
  Challenge as UsChallengePb,
  ChallengeRequest as UsChallengeRequestPb,
  ChallengeResponse as UsChallengeResponsePb,
} from "@repo/protobuf/gen/kikoff/api/mfa_pb";
import { User as CanadaUserPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/user_pb";
import {
  AccountType as UsaAccountTypePb,
  PersonalInfo as UsaPersonalInfoPb,
  User as UsaUserPb,
} from "@repo/protobuf/gen/web/public/user_pb";
import { MessageFrom } from "@repo/protobuf/utils";
import Telemetry from "@repo/telemetry";
import Address from "@repo/utils/Address";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";
import FullName from "@repo/utils/FullName";
import Locale from "@repo/utils/Locale";
import Tagged from "@repo/utils/Tagged";
import UString from "@repo/utils/UString";

import { AddressUsa } from "./Address.js";
import { FullNameCanada, FullNameUsa } from "./FullName.js";
import { LocaleCanada } from "./Locale.js";

type User = {
  id: User.Id;
  status: User.Status;
  pii: User.Pii.Readable;
  active: boolean;
  locale: Locale;
  expectedLtv?: number;
  provisionedFor?: string;
};

namespace User {
  export type Id = Tagged<string, "User">;
  export namespace Id {
    export const format = (id: Id) =>
      import.meta.env.API_ADAPTER === "canada"
        ? UString.splitAt(id, [6, 10, 14]).join("-")
        : id;
  }
  export type Status = "ONBOARDING" | "ONBOARDED";
  export type Credentials = { email: string; password: string };
  export type Pii = {
    email: string;
    name: FullName;
    phone: string;
    address: Address;
    birthDate: string;
    nationalId?: string;
    emailConfirmed: boolean;
  };

  export namespace Pii {
    export type Readable = Omit<Pii, "birthDate" | "nationalId">;
  }

  export namespace Mfa {
    export type Method = "phone" | "email";
    export type Purpose = "userLogin";

    export type Summary = Pick<Challenge, "method" | "description">;
    export type Challenge = {
      token: string;
      method: Method;
      description: string;
      codeLength: number;
    };
    export namespace Challenge {
      export class Failure extends Error {
        name = "MfaChallengeFailure";
        message = t`MFA failed.`;
        constructor(public readonly reason: Failure.Reason) {
          super();
        }
      }
      export namespace Failure {
        export type Reason = "tooManyAttempts" | "unknown";
      }
    }
    export namespace Verification {
      export class Invalid extends Error {
        name = "MfaInvalidVerification";
      }
    }
  }
}
export default User;

export namespace UserUsa {
  // This is intended to be used for users from BASIC_INFO scope, add other
  // fields directly to feature store
  export function normalize(user: MessageFrom<UsaUserPb>): User {
    const status = Enum.keyOf(UsaUserPb.Status, user.status);
    if (status !== "ONBOARDING" && status !== "ONBOARDED")
      throw new Error(`Unexpected ${status} status in transaction entity.`);

    return {
      id: user.token as User.Id,
      status,
      pii: Pii.normalize(user.info!),
      expectedLtv: user.expectedLtv,
      // TODO: Need this from backend
      active: true,
      locale:
        Locale.list.find((locale) => user.locale.startsWith(locale)) ?? "en",
      provisionedFor:
        user.accountType?.type === UsaAccountTypePb.Type.GUARANTORS
          ? "guarantors"
          : undefined,
    };
  }
  export namespace Pii {
    export const normalize = (
      info: MessageFrom<UsaPersonalInfoPb>
    ): User.Pii.Readable => ({
      email: info.email,
      name: FullNameUsa.normalize(info),
      phone: info.phone,
      address: AddressUsa.normalize(info),
      emailConfirmed: info.emailConfirmed,
    });
  }
  export namespace Mfa {
    export const normalizeResponse = (
      response: MessageFrom<UsChallengeResponsePb>
    ): {
      challenge: User.Mfa.Challenge;
      alternatives: User.Mfa.Summary[];
    } => {
      return {
        challenge: {
          token: response.challengeToken,
          method: Method.normalize(response.challenge!.carrier),
          description: response.challenge!.description,
          codeLength: response.challenge!.codeLength,
        },
        alternatives: response.alternatives.map((alternative) => ({
          method: Method.normalize(alternative.carrier),
          description: alternative.description,
        })),
      };
    };
    export namespace Method {
      export const normalize = (
        proto: UsChallengePb.Carrier
      ): User.Mfa.Method => {
        const method = Case.fromConstant(
          Enum.keyOf(UsChallengePb.Carrier, proto)
        ).toCamel();

        if (method === "unknown")
          throw new Error("Unexpected UNKNOWN carrier.");

        return method;
      };
      export const denormalize = (
        method: User.Mfa.Method
      ): UsChallengePb.Carrier =>
        UsChallengePb.Carrier[Case.fromCamel(method).toConstant()];
    }
    export namespace Purpose {
      export const denormalize = (
        purpose: User.Mfa.Purpose
      ): UsChallengeRequestPb.Purpose =>
        UsChallengeRequestPb.Purpose[Case.fromCamel(purpose).toConstant()];
    }
    export namespace Challenge {
      export namespace Failure {
        export const normalize = (
          failure: UsChallengeResponsePb.Failure.Reason
        ): User.Mfa.Challenge.Failure => {
          const reason = Case.fromConstant(
            Enum.keyOf(UsChallengeResponsePb.Failure.Reason, failure)
          ).toCamel();

          if (reason !== "tooManyAttempts") {
            Telemetry.captureError(
              new Error(`Unexpected MFA failure reason "${reason}".`)
            );
            return new User.Mfa.Challenge.Failure("unknown");
          }

          return new User.Mfa.Challenge.Failure(
            reason as User.Mfa.Challenge.Failure.Reason
          );
        };
      }
    }
  }
}

export namespace UserCanada {
  export function normalize(user: MessageFrom<CanadaUserPb>): User {
    const status = Enum.keyOf(
      CanadaUserPb.OnboardingStatus,
      user.onboardingStatus
    );
    if (status === "UNKNOWN")
      throw new Error("Unexpected UNKNOWN onboarding status.");

    return {
      id: user.id as User.Id,
      status,
      pii: Pii.normalize(user.pii!),
      active: user.active,
      locale: LocaleCanada.normalize(user.locale) || "en",
    };
  }
  export namespace Pii {
    export const normalize = (
      pii: MessageFrom<CanadaUserPb.Pii>
    ): User.Pii.Readable => ({
      email: pii.email,
      name: FullNameCanada.normalize(pii.name!),
      phone: pii.phone,
      address: pii.address!,
      emailConfirmed: pii.emailConfirmed!,
    });
  }
}
